"use client";

import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PICTURE_WIDTHS } from "./picture-widths";
import { CHART_HEIGHTS, type ChartData } from "./chart-data";

const COLOURS = ["var(--primary)", "var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)"];

/** The colour of the series (or pie slice) `i`: the one picked, else the theme's. */
export function colourOf(chart: ChartData, i: number): string {
  return chart.colors[i] || COLOURS[i % COLOURS.length];
}

/** Draws a chart block (bars, line or pie) in the theme's colours, one bar or line per series. Pies use the first series. Used by the editor and by the public page. */
export function ChartView({ chart }: { chart: ChartData }) {
  const width = Math.max(1, ...chart.rows.map((row) => row.values.length));
  const names = Array.from({ length: width }, (_, i) => chart.series[i] || String(i + 1));
  const data = chart.rows.map((row) => ({ name: row.label, ...Object.fromEntries(names.map((n, i) => [n, row.values[i]])) }));
  const axes = <><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Legend /></>;
  return (
    <figure className="my-4" style={{ width: PICTURE_WIDTHS.find((w) => w === chart.width) }}>
      {chart.title && <figcaption className="mb-2 font-semibold">{chart.title}</figcaption>}
      <div className="w-full" style={{ height: CHART_HEIGHTS.find((h) => h === chart.height) ?? 288 }}>
        <ResponsiveContainer>
          {chart.chartType === "pie" ? (
            <PieChart><Pie data={data} dataKey={names[0]} nameKey="name" label>{data.map((row, i) => <Cell key={row.name} fill={colourOf(chart, i)} />)}</Pie><Tooltip /><Legend /></PieChart>
          ) : chart.chartType === "line" ? (
            <LineChart data={data}>{axes}{names.map((n, i) => <Line key={n} dataKey={n} stroke={colourOf(chart, i)} strokeWidth={2} />)}</LineChart>
          ) : (
            <BarChart data={data}>{axes}{names.map((n, i) => <Bar key={n} dataKey={n} fill={colourOf(chart, i)} />)}</BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </figure>
  );
}
